import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import Modal from '../../../components/common/Modal';
import ImageCropModal from '../../../components/common/ImageCropModal';
import { useS3Image } from '../../../hooks/useS3Image';

import { validateFullName, validatePreferredName } from '../../../utils/nameFilter';
import { validatePhoneNumber, validateEmail, formatPhoneNumber, formatPhoneNumberForAPI } from '../../../utils/validationUtils';

const ProfileSection = ({ 
  user, 
  fileInputRef, 
  setUser, 
  userRole,
  // Phone verification modal props
  showPhoneVerificationModal,
  setShowPhoneVerificationModal,
  pendingPhoneNumber,
  setPendingPhoneNumber,
  onPhoneVerificationConfirm,
  // Email verification modal props
  showEmailVerificationModal,
  setShowEmailVerificationModal,
  pendingEmail,
  setPendingEmail,
  onEmailVerificationConfirm,
  variant = 'default', // 'default' | 'embedded' - embedded for AccountModal tab
}) => {
  const { darkMode } = useTheme();
  const { handleImageError, refreshProfileImageUrl } = useS3Image(user, setUser);
  const [name, setName] = useState(user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '');
  const [callName, setCallName] = useState(user?.preferredName || user?.firstName || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [email, setEmail] = useState(user?.email || '');
  const [companyName, setCompanyName] = useState(user?.company?.name || '');
  const [companyNickname, setCompanyNickname] = useState(user?.company?.nickname || '');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyNickname, setNewCompanyNickname] = useState('');
  const [location, setLocation] = useState(user?.company?.location || '');
  const [newLocation, setNewLocation] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLoadingLocationSuggestions, setIsLoadingLocationSuggestions] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const locationInputRef = useRef(null);
  const locationSuggestionsRef = useRef(null);
  const [shouldUpdateLocationOnBlur, setShouldUpdateLocationOnBlur] = useState(true);
  const isSendingPhoneVerification = useRef(false);
  const phoneInputEnterPressed = useRef(false);
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showCompanyNicknameModal, setShowCompanyNicknameModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [companyConfirmText, setCompanyConfirmText] = useState('');
  const [companyNicknameConfirmText, setCompanyNicknameConfirmText] = useState('');
  const [locationConfirmText, setLocationConfirmText] = useState('');
  const [lastLogin, setLastLogin] = useState(user?.lastLogin || null);
  const [createdAt, setCreatedAt] = useState(user?.createdAt || null);
  
  // New state for image crop modal
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);

  // Update state when user data changes (on refresh or login)
  useEffect(() => {
    console.log('useEffect triggered with user:', user);
    if (user) {
      const newName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '';
      const newCallName = user?.preferredName || user?.firstName || '';
      const newPhoneNumber = user?.phoneNumber || '';
      const newEmail = user?.email || '';
      const newCompanyName = user?.company?.name || '';
      const newCompanyNickname = user?.company?.nickname || '';
      const newLocation = user?.company?.location || '';
      const newLastLogin = user?.lastLogin || null;
      const newCreatedAt = user?.createdAt || null;
      
      console.log('Setting state values:', {
        newName,
        newCallName,
        newPhoneNumber,
        newEmail,
        newCompanyName,
        newCompanyNickname,
        newLocation,
        newLastLogin,
        newCreatedAt
      });
      
      setName(newName);
      setCallName(newCallName);
      setPhoneNumber(newPhoneNumber);
      setEmail(newEmail);
      setCompanyName(newCompanyName);
      setCompanyNickname(newCompanyNickname);
      setLocation(newLocation);
      setLastLogin(newLastLogin);
      setCreatedAt(newCreatedAt);
    }
  }, [user]);

  // Refresh stale signed profile image URLs (sidebar does this on img error; account modal needs it too)
  useEffect(() => {
    if (!user?.profileImage?.key) return;

    const updatedAt = user.profileImage.updatedAt ? new Date(user.profileImage.updatedAt).getTime() : 0;
    const sixDaysMs = 6 * 24 * 60 * 60 * 1000;
    if (!updatedAt || Date.now() - updatedAt > sixDaysMs) {
      refreshProfileImageUrl();
    }
  }, [user?.id, user?.profileImage?.key, user?.profileImage?.updatedAt, refreshProfileImageUrl]);

  // Toast style based on dark mode
  const toastStyle = {
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
    loading: {
      style: {
        background: darkMode ? '#1e3a8a' : '#eff6ff',
        color: darkMode ? '#fff' : '#1e40af',
        border: `1px solid ${darkMode ? '#1e40af' : '#dbeafe'}`,
      },
    },
  };

  // Handle file selection - show crop modal instead of uploading directly
  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or GIF)', toastStyle.error);
      return;
    }

    // 5MB max size
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB', toastStyle.error);
      return;
    }

    // Store the file and show crop modal
    setSelectedImageFile(file);
    setShowCropModal(true);
  };

  // Handle cropped image confirmation
  const handleCroppedImageConfirm = async (croppedFile) => {
    setShowCropModal(false);
    setSelectedImageFile(null);
    
    // Now upload the cropped image
    await uploadCroppedImage(croppedFile);
  };

  // Handle crop modal close
  const handleCropModalClose = () => {
    setShowCropModal(false);
    setSelectedImageFile(null);
    
    // Clear the input so the same file can be uploaded again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload the cropped image to S3
  const uploadCroppedImage = async (croppedFile) => {
    setIsUploadingImage(true);
    const toastId = toast.loading('Uploading image...', toastStyle);

    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('profileImage', croppedFile);

      // Get token for auth
      const token = localStorage.getItem('token');
      
      // Upload image to S3 through our backend API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/upload-profile-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      // Update the user in local storage and state
      const { profileImage } = response.data;
      const updatedUser = { ...user, profileImage };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      toast.success('Profile image updated successfully', { id: toastId, ...toastStyle.success });
    } catch (error) {
      console.error('Error uploading profile image:', error);
      toast.error('Failed to upload image. Please try again.', { id: toastId, ...toastStyle.error });
    } finally {
      setIsUploadingImage(false);
      
      // Clear the input so the same file can be uploaded again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle S3 image upload (kept for backwards compatibility, but now shows crop modal)
  const handleImageUpload = async (e) => {
    // Use the new file selection handler
    handleImageFileSelect(e);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close suggestions if clicking outside the location input and suggestions
      if (
        locationInputRef.current && 
        !locationInputRef.current.contains(event.target) && 
        locationSuggestionsRef.current && 
        !locationSuggestionsRef.current.contains(event.target)
      ) {
        setShowLocationSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNameUpdate = async (newName) => {
    if (isUpdating) return; // Prevent multiple simultaneous updates
    
    try {
      setIsUpdating(true);
      toast.loading('Updating name...', { id: 'nameUpdate', ...toastStyle });
      
      const [firstName, ...lastNameParts] = newName.trim().split(' ');
      const lastName = lastNameParts.join(' ');
      
      if (!firstName || !lastName) {
        toast.error('Please enter both first and last name', { id: 'nameUpdate', ...toastStyle.error });
        setIsUpdating(false);
        return;
      }

      // Validate the name before sending to backend
      const nameValidation = validateFullName(firstName, lastName);
      if (!nameValidation.isValid) {
        toast.error(nameValidation.reason, { id: 'nameUpdate', ...toastStyle.error });
        setIsUpdating(false);
        return;
      }

      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { 
          firstName,
          lastName
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update was successful
      const updatedUser = { ...user, firstName, lastName };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setName(`${firstName} ${lastName}`); // Update the input field
      toast.success('Name updated successfully', { id: 'nameUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating name:', error);
      if (error.response?.status === 404) {
        // If we get a 404, the update might have still worked (due to the route issue)
        const updatedUser = { ...user, firstName: newName.split(' ')[0], lastName: newName.split(' ').slice(1).join(' ') };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setName(newName);
        toast.success('Name updated successfully', { id: 'nameUpdate', ...toastStyle.success });
      } else {
        toast.error('Failed to update name. Please try again.', { id: 'nameUpdate', ...toastStyle.error });
        // Don't reset the name if it actually succeeded
        if (error.response?.status !== 404) {
          setName(user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '');
        }
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBlur = (e, currentValue, updateFn) => {
    console.log('handleBlur called:', { 
      inputValue: e.target.value, 
      currentValue, 
      isEqual: e.target.value === currentValue 
    });
    if (e.target.value !== currentValue) {
      console.log('Values are different, calling updateFn');
      updateFn(e.target.value);
    } else {
      console.log('Values are same, no update needed');
    }
  };

  const handleCallNameUpdate = async (newCallName) => {
    console.log('handleCallNameUpdate called with:', newCallName);
    if (isUpdating) return; // Prevent multiple simultaneous updates
    
    try {
      setIsUpdating(true);
      toast.loading('Updating call name...', { id: 'callNameUpdate', ...toastStyle });

      // Validate the preferred name before sending to backend
      const preferredNameValidation = validatePreferredName(newCallName.trim());
      if (!preferredNameValidation.isValid) {
        toast.error(preferredNameValidation.reason, { id: 'callNameUpdate', ...toastStyle.error });
        setIsUpdating(false);
        return;
      }

      const token = localStorage.getItem('token');
      console.log('Making API call to update preferredName');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { 
          preferredName: newCallName.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API call successful');
      console.log('API response:', response.data);
      // Update was successful
      const updatedUser = { ...user, preferredName: newCallName.trim() };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setCallName(newCallName.trim()); // Update the input field
      toast.success('Call name updated successfully', { id: 'callNameUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating call name:', error);
      if (error.response?.status === 404) {
        // If we get a 404, the update might have still worked (due to the route issue)
        const updatedUser = { ...user, preferredName: newCallName.trim() };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setCallName(newCallName.trim());
        toast.success('Call name updated successfully', { id: 'callNameUpdate', ...toastStyle.success });
      } else {
        toast.error('Failed to update call name. Please try again.', { id: 'callNameUpdate', ...toastStyle.error });
        // Don't reset the call name if it actually succeeded
        if (error.response?.status !== 404) {
          setCallName(user?.preferredName || user?.firstName || '');
        }
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePhoneNumberUpdate = async (newPhoneNumber) => {
    console.log('handlePhoneNumberUpdate called with:', newPhoneNumber);
    if (isUpdating || isSendingPhoneVerification.current) {
      console.log('Phone update already in progress, skipping duplicate call');
      return;
    }
    
    try {
      // Set flag to prevent duplicate calls
      isSendingPhoneVerification.current = true;
      
      // Validate the phone number
      const phoneValidation = validatePhoneNumber(newPhoneNumber.trim());
      if (!phoneValidation.isValid) {
        toast.error(phoneValidation.reason, toastStyle.error);
        return;
      }

      // Format the phone number for API
      const formattedPhone = formatPhoneNumberForAPI(newPhoneNumber.trim());
      
      // Normalize both phone numbers for comparison (remove all non-digits except +)
      const normalizePhone = (phone) => {
        if (!phone) return '';
        // Remove all non-digits, then ensure +1 prefix format
        const digitsOnly = phone.replace(/\D/g, '');
        if (digitsOnly.length === 10) {
          return `+1${digitsOnly}`;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
          return `+${digitsOnly}`;
        }
        return phone.startsWith('+') ? phone : `+${phone.replace(/\D/g, '')}`;
      };
      
      const normalizedNew = normalizePhone(formattedPhone);
      const normalizedCurrent = normalizePhone(user?.phoneNumber);
      
      // Check if the new phone number is different from the current one
      if (normalizedNew === normalizedCurrent) {
        console.log('Phone number unchanged, no verification needed');
        return;
      }
      
      // Store the pending phone number and show verification modal
      setPendingPhoneNumber(formattedPhone);
      setShowPhoneVerificationModal(true);
      
      // Send verification code
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/phone/send-verification`, {
        phoneNumber: formattedPhone,
        channel: 'sms',
        isProfileUpdate: true
      });
      
      toast.success('Verification code sent to your new phone number', toastStyle.success);
    } catch (error) {
      console.error('Error initiating phone number update:', error);
      toast.error('Failed to send verification code. Please try again.', toastStyle.error);
      // Reset modal state on error
      setShowPhoneVerificationModal(false);
      setPendingPhoneNumber('');
    } finally {
      // Reset flag after a short delay to allow modal to open
      setTimeout(() => {
        isSendingPhoneVerification.current = false;
      }, 1000);
    }
  };



  const handleEmailUpdate = async (newEmail) => {
    console.log('handleEmailUpdate called with:', newEmail);
    if (isUpdating) return;
    
    try {
      // Validate the email
      const emailValidation = validateEmail(newEmail.trim());
      if (!emailValidation.isValid) {
        toast.error(emailValidation.reason, toastStyle.error);
        return;
      }

      // Check if the new email is different from the current one
      if (newEmail.trim().toLowerCase() === user?.email?.toLowerCase()) {
        console.log('Email unchanged, no verification needed');
        return;
      }

      // Store the pending email and show verification modal
      setPendingEmail(newEmail.trim());
      setShowEmailVerificationModal(true);
      
      // Send verification code
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/phone/email/send-verification-profile`, {
        email: newEmail.trim()
      });
      
      toast.success('Verification code sent to your new email address', toastStyle.success);
    } catch (error) {
      console.error('Error initiating email update:', error);
      toast.error('Failed to send verification code. Please try again.', toastStyle.error);
    }
  };



  const handleCompanyChange = (e) => {
    setCompanyName(e.target.value);
  };

  const handleCompanyBlur = (e) => {
    if (e.target.value !== user?.company?.name && userRole !== 'member') {
      setNewCompanyName(e.target.value);
      setShowCompanyModal(true);
    }
  };

  const handleCompanyKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.value !== user?.company?.name && userRole !== 'member') {
      e.preventDefault();
      setNewCompanyName(e.target.value);
      setShowCompanyModal(true);
    }
  };

  const handleCompanyNicknameChange = (e) => {
    setCompanyNickname(e.target.value);
  };

  const handleCompanyNicknameBlur = (e) => {
    if (e.target.value !== user?.company?.nickname && userRole !== 'member') {
      setNewCompanyNickname(e.target.value);
      setShowCompanyNicknameModal(true);
    }
  };

  const handleCompanyNicknameKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.value !== user?.company?.nickname && userRole !== 'member') {
      e.preventDefault();
      setNewCompanyNickname(e.target.value);
      setShowCompanyNicknameModal(true);
    }
  };

  const handleCompanyModalConfirm = () => {
    // Check if the user typed 'confirm'
    if (companyConfirmText.toLowerCase() !== 'confirm') {
      toast.error('Please type "confirm" to proceed', toastStyle.error);
      return;
    }

    handleCompanyUpdate(newCompanyName);
    setShowCompanyModal(false);
    setCompanyConfirmText('');
  };

  const handleCompanyModalClose = () => {
    setShowCompanyModal(false);
    setCompanyConfirmText('');
    // Reset company name to original
    setCompanyName(user?.company?.name || '');
  };

  const handleCompanyNicknameModalConfirm = () => {
    // Check if the user typed 'confirm'
    if (companyNicknameConfirmText.toLowerCase() !== 'confirm') {
      toast.error('Please type "confirm" to proceed', toastStyle.error);
      return;
    }

    handleCompanyNicknameUpdate(newCompanyNickname);
    setShowCompanyNicknameModal(false);
    setCompanyNicknameConfirmText('');
  };

  const handleCompanyNicknameModalClose = () => {
    setShowCompanyNicknameModal(false);
    setCompanyNicknameConfirmText('');
    // Reset company nickname to original
    setCompanyNickname(user?.company?.nickname || '');
  };

  const handleCompanyUpdate = async (newCompany) => {
    if (isUpdating) return;
    
    try {
      setIsUpdating(true);
      toast.loading('Updating organization...', { id: 'companyUpdate', ...toastStyle });
      
      if (!newCompany.trim()) {
        toast.error('Organization name cannot be empty', { id: 'companyUpdate', ...toastStyle.error });
        setIsUpdating(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      // First get the organization data to obtain the companyId
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization/members`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const organizationId = orgResponse.data.organization?.id;
      
      console.log('Organization data:', orgResponse.data);
      console.log('Organization ID:', organizationId);
      
      // Check if user has a companyId from the organization endpoint
      if (organizationId) {
        // FIRST: Update the Company collection document using companyId from organization
        console.log('Company update data:', { companyId: organizationId, name: newCompany.trim() });
        await axios.put(
          `${process.env.REACT_APP_API_URL}/companies`,
          {
            companyId: organizationId.toString(), // Convert to string to ensure proper format
            name: newCompany.trim()
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        console.log('No companyId found from organization API, skipping company update');
      }
      
      // SECOND: Update the user's company field
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { 
          companyName: newCompany.trim(),
          company: {
            name: newCompany.trim(),
            nickname: user.company?.nickname || '',
            location: user.company?.location || ''
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setUser({
        ...user,
        companyName: newCompany.trim(),
        company: {
          ...user.company,
          name: newCompany.trim()
        }
      });
      
      toast.success('Organization name updated successfully', { id: 'companyUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating organization:', error, error.response?.data);
      toast.error('Failed to update organization name. Please try again.', { id: 'companyUpdate', ...toastStyle.error });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCompanyNicknameUpdate = async (newNickname) => {
    if (isUpdating) return;
    
    try {
      setIsUpdating(true);
      toast.loading('Updating organization nickname...', { id: 'companyNicknameUpdate', ...toastStyle });

      const token = localStorage.getItem('token');
      
      // First get the organization data to obtain the companyId
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization/members`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const organizationId = orgResponse.data.organization?.id;
      
      console.log('Organization data for nickname update:', orgResponse.data);
      console.log('Organization ID for nickname update:', organizationId);
      
      // Check if user has a companyId from the organization endpoint
      if (organizationId) {
        // FIRST: Update the Company collection document using companyId from organization
        console.log('Company nickname update data:', { companyId: organizationId, nickname: newNickname.trim() });
        const companyResponse = await axios.put(
          `${process.env.REACT_APP_API_URL}/companies`,
          {
            companyId: organizationId.toString(), // Convert to string to ensure proper format
            nickname: newNickname.trim()
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        console.log('Company update response:', companyResponse.data);
      } else {
        console.log('No companyId found from organization API, skipping company nickname update');
      }
      
      // SECOND: Update the user's company field
      console.log('Updating user company field with nickname:', newNickname.trim());
      const userResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { 
          company: {
            name: user.company?.name || '',
            location: user.company?.location || ''
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('User update response:', userResponse.data);

      setUser({
        ...user,
        company: {
          ...user.company,
          nickname: newNickname.trim()
        }
      });
      
      toast.success('Organization nickname updated successfully', { id: 'companyNicknameUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating organization nickname:', error, error.response?.data);
      toast.error('Failed to update organization nickname. Please try again.', { id: 'companyNicknameUpdate', ...toastStyle.error });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLocationChange = async (e) => {
    const value = e.target.value;
    setLocation(value);
    
    // Set flag to not update on blur since user is typing
    setShouldUpdateLocationOnBlur(false);
    
    if (value.trim().length > 2) {
      setIsLoadingLocationSuggestions(true);
      try {
        const response = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json`,
          {
            params: {
              access_token: 'pk.eyJ1IjoiYmxha2VvcHAiLCJhIjoiY204eHphcDdmMDl3cjJscTE3cGdvaHh0dyJ9.BQ5kRvxICCEIXE1Vgpl1MA',
              types: 'place',
              limit: 5
            }
          }
        );
        setLocationSuggestions(response.data.features.map(feature => feature.place_name));
        setShowLocationSuggestions(true);
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
      }
      setIsLoadingLocationSuggestions(false);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  const handleLocationSuggestionClick = (suggestion) => {
    // First update the UI
    setLocation(suggestion);
    setShowLocationSuggestions(false);
    setShouldUpdateLocationOnBlur(false); // Prevent the blur event from triggering an update
    
    // Then update the backend
    handleLocationUpdate(suggestion);
  };

  const handleLocationUpdate = async (newLocation) => {
    if (isUpdating) return;
    
    try {
      setIsUpdating(true);
      toast.loading('Updating location...', { id: 'locationUpdate', ...toastStyle });
      
      if (!newLocation.trim()) {
        toast.error('Location cannot be empty', { id: 'locationUpdate', ...toastStyle.error });
        setIsUpdating(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      // First get the organization data to obtain the companyId
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization/members`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const organizationId = orgResponse.data.organization?.id;
      
      console.log('Organization data for location update:', orgResponse.data);
      console.log('Organization ID for location update:', organizationId);
      
      // Check if user has a companyId from the organization endpoint
      if (organizationId) {
        // FIRST: Update the Company collection document using companyId from organization
        console.log('Location update data:', { companyId: organizationId, location: newLocation.trim() });
        await axios.put(
          `${process.env.REACT_APP_API_URL}/companies`,
          {
            companyId: organizationId.toString(), // Convert to string to ensure proper format
            location: newLocation.trim()
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        console.log('No companyId found from organization API, skipping company location update');
      }

      // SECOND: Update the user's location field
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { 
          location: newLocation.trim(),
          company: {
            name: user.company?.name || '',
            location: newLocation.trim()
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setUser({
        ...user,
        location: newLocation.trim(),
        company: {
          ...user.company,
          location: newLocation.trim()
        }
      });
      
      toast.success('Location updated successfully', { id: 'locationUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating location:', error, error.response?.data);
      toast.error('Failed to update location. Please try again.', { id: 'locationUpdate', ...toastStyle.error });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyDown = (e, updateFn) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      updateFn(e.target.value);
      setShowLocationSuggestions(false);
    }
  };

  // Add new functions for location modal
  const handleLocationBlurConfirmation = () => {
    if (location !== user?.company?.location && userRole !== 'member') {
      setNewLocation(location);
      setShowLocationModal(true);
    }
  };

  const handleLocationModalConfirm = () => {
    // Check if the user typed 'confirm'
    if (locationConfirmText.toLowerCase() !== 'confirm') {
      toast.error('Please type "confirm" to proceed', toastStyle.error);
      return;
    }

    handleLocationUpdate(newLocation);
    setShowLocationModal(false);
    setLocationConfirmText('');
  };

  const handleLocationModalClose = () => {
    setShowLocationModal(false);
    setLocationConfirmText('');
    // Reset location to original
    setLocation(user?.company?.location || '');
  };

  // Update existing handleLocationBlur function
  const handleLocationBlur = () => {
    if (shouldUpdateLocationOnBlur && location !== user?.company?.location) {
      // Only call location update from blur if we didn't just click a suggestion
      if (userRole !== 'member') {
        handleLocationBlurConfirmation();
      } else {
        setTimeout(() => {
          handleLocationUpdate(location);
        }, 100);
      }
    }
    
    // Reset the flag for next time
    setShouldUpdateLocationOnBlur(true);
  };

  // Format dates to display in user's timezone
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    // Format as "December 5, 2025"
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    // Format as "December 5 at 10:02PM"
    const datePart = date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric' 
    });
    const timePart = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).replace(' ', '');
    return `${datePart} at ${timePart}`;
  };

  const isEmbedded = variant === 'embedded';

  const inputClass = isEmbedded
    ? 'account-modal-input w-full px-3 py-2.5 border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#444] focus:border-gray-300 dark:focus:border-[#444] text-sm transition-colors disabled:opacity-60'
    : 'mt-2 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black dark:bg-[#181818] dark:text-white dark:border-[#444444]';
  const labelClass = isEmbedded
    ? 'block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5'
    : 'block text-sm font-medium text-[#181818] dark:text-gray-400';
  const avatarSize = isEmbedded ? 'w-16 h-16' : 'w-20 h-20';

  return (
    <>
      <div 
        className={isEmbedded ? 'profile-section-embedded p-0' : 'profile-section-root rounded-2xl mt-2 p-6 border border-gray-200 dark:text-white dark:border-[#444444]'}
        style={isEmbedded ? { fontFamily: 'var(--chat-font-family)' } : {}}
      >
        {!isEmbedded && <h2 className="text-xl font-semibold mb-6">Profile</h2>}
        
        <div className={isEmbedded ? 'space-y-5' : 'space-y-6'}>
          <div className={`flex gap-4 ${isEmbedded ? 'items-center pb-5 border-b border-gray-100 dark:border-[#2a2a2a]' : 'items-start'}`}>
            <div className="profile-avatar group relative flex-shrink-0 cursor-pointer">
              {user?.profileImage?.url ? (
                <img 
                  src={user.profileImage.url}
                  alt="Profile" 
                  className={`profile-avatar-image ${avatarSize} rounded-full object-cover transition-opacity group-hover:opacity-50`}
                  onError={handleImageError}
                />
              ) : (
                <div className={`profile-avatar-placeholder ${avatarSize} rounded-full bg-gray-100 dark:bg-[#282828] flex items-center justify-center text-gray-700 dark:text-gray-300 text-lg font-medium transition-opacity group-hover:opacity-50`}>
                  {initials || '?'}
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                className="hidden"
                accept="image/jpeg,image/png,image/gif"
                disabled={isUploadingImage}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 dark:bg-black/40"
                disabled={isUploadingImage}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className={`flex gap-4 ${isEmbedded ? 'flex-col sm:flex-row' : ''}`}>
                <div className={isEmbedded ? 'flex-1' : 'flex-[1.5]'}>
                  <label className={labelClass}>Full name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={(e) => handleBlur(e, name, handleNameUpdate)}
                    onKeyDown={(e) => handleKeyDown(e, handleNameUpdate)}
                    className={inputClass}
                    placeholder="Enter your full name"
                    disabled={isUpdating}
                  />
                </div>
                
                <div className={isEmbedded ? 'flex-1' : 'flex-1'}>
                  <label className={labelClass}>What should we call you?</label>
                  <input
                    type="text"
                    value={callName}
                    onChange={(e) => setCallName(e.target.value)}
                    onBlur={(e) => handleBlur(e, callName, handleCallNameUpdate)}
                    onKeyDown={(e) => handleKeyDown(e, handleCallNameUpdate)}
                    className={`${inputClass} ${callName === user?.firstName ? 'text-gray-400 dark:text-gray-500' : ''}`}
                    placeholder="Nickname or preferred name"
                    disabled={isUpdating}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`flex gap-4 ${isEmbedded ? 'pb-5 border-b border-gray-100 dark:border-[#2a2a2a] flex-col sm:flex-row' : ''}`}>
            <div className="flex-1">
              <label className={labelClass}>Phone number</label>
              <input
                type="tel"
                value={formatPhoneNumber(phoneNumber)}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onBlur={(e) => {
                  // Skip blur handler if Enter was just pressed (to prevent duplicate calls)
                  if (phoneInputEnterPressed.current) {
                    phoneInputEnterPressed.current = false;
                    return;
                  }
                  
                  // For phone numbers, we need to compare the raw values, not formatted
                  // Compare against the original user phone number, not the state (which may be formatted)
                  const rawInputValue = e.target.value.replace(/\D/g, '');
                  const rawCurrentValue = (user?.phoneNumber || '').replace(/\D/g, '');
                  if (rawInputValue !== rawCurrentValue && rawInputValue.length >= 10) {
                    handlePhoneNumberUpdate(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Set flag to prevent blur handler from also firing
                    phoneInputEnterPressed.current = true;
                    
                    // Compare against the original user phone number, not the state (which may be formatted)
                    const rawInputValue = e.target.value.replace(/\D/g, '');
                    const rawCurrentValue = (user?.phoneNumber || '').replace(/\D/g, '');
                    if (rawInputValue !== rawCurrentValue && rawInputValue.length >= 10) {
                      handlePhoneNumberUpdate(e.target.value);
                    } else {
                      // Reset flag if we're not calling handlePhoneNumberUpdate
                      phoneInputEnterPressed.current = false;
                    }
                  }
                }}
                className={inputClass}
                placeholder="Enter your phone number"
                disabled={isUpdating}
              />
            </div>
            
            <div className="flex-1">
              <label className={labelClass}>Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => handleBlur(e, email, handleEmailUpdate)}
                onKeyDown={(e) => handleKeyDown(e, handleEmailUpdate)}
                className={inputClass}
                placeholder="Enter your email address"
                disabled={isUpdating}
              />
            </div>
          </div>

          <div className={isEmbedded ? 'pb-5 border-b border-gray-100 dark:border-[#2a2a2a]' : ''}>
            {userRole !== 'member' ? (
              <div className={`flex gap-4 ${isEmbedded ? 'flex-col sm:flex-row' : ''}`}>
                <div className="flex-1">
                  <label className={labelClass}>Organization name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={handleCompanyChange}
                    onBlur={handleCompanyBlur}
                    onKeyDown={handleCompanyKeyDown}
                    className={inputClass}
                    placeholder="Enter your organization name"
                    disabled={isUpdating}
                  />
                </div>
                
                <div className="flex-1">
                  <label className={labelClass}>Organization nickname</label>
                  <input
                    type="text"
                    value={companyNickname}
                    onChange={handleCompanyNicknameChange}
                    onBlur={handleCompanyNicknameBlur}
                    onKeyDown={handleCompanyNicknameKeyDown}
                    className={inputClass}
                    placeholder=""
                    disabled={isUpdating}
                  />
                </div>
              </div>
            ) : (
              <>
                <label className={labelClass}>Organization name</label>
                <input
                  type="text"
                  value={companyNickname || companyName}
                  onChange={handleCompanyChange}
                  onBlur={handleCompanyBlur}
                  onKeyDown={handleCompanyKeyDown}
                  className={`${inputClass} bg-gray-100 dark:bg-[#222222] cursor-not-allowed`}
                  placeholder="Enter your organization name"
                  disabled={true}
                />
              </>
            )}
          </div>

          {userRole !== 'member' && (
            <div className={`relative ${isEmbedded ? 'pb-5 border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}>
              <label className={labelClass}>Location</label>
              <input
                ref={locationInputRef}
                type="text"
                value={location}
                onChange={handleLocationChange}
                onBlur={handleLocationBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLocationUpdate(location);
                    setShowLocationSuggestions(false);
                  } else if (e.key === 'ArrowDown' && showLocationSuggestions && locationSuggestions.length > 0) {
                    if (locationSuggestionsRef.current?.firstChild) {
                      locationSuggestionsRef.current.firstChild.focus();
                    }
                  }
                }}
                className={inputClass}
                placeholder="Enter your location"
                disabled={isUpdating}
              />
              
              {showLocationSuggestions && locationSuggestions.length > 0 && (
                <div 
                  ref={locationSuggestionsRef}
                  className={`absolute z-10 mt-1 w-full overflow-hidden ${isEmbedded ? 'account-modal-shape-sm bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] shadow-lg' : 'bg-white dark:bg-[#282828] shadow-lg rounded-lg border border-gray-300 dark:border-[#444444]'}`}
                >
                  {locationSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleLocationSuggestionClick(suggestion)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleLocationSuggestionClick(suggestion);
                        }
                      }}
                      className={`px-3 py-2 cursor-pointer text-sm focus:outline-none ${isEmbedded ? 'hover:bg-gray-50 dark:hover:bg-[#252525] text-gray-700 dark:text-gray-200 focus:bg-gray-50 dark:focus:bg-[#252525]' : 'hover:bg-gray-100 dark:hover:bg-[#333333] text-gray-800 dark:text-gray-200 focus:bg-gray-100 dark:focus:bg-[#333333]'}`}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
              
              {isLoadingLocationSuggestions && (
                <div className={`absolute z-10 mt-1 w-full px-3 py-2 text-center text-sm ${isEmbedded ? 'account-modal-shape-sm bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] text-gray-600 dark:text-gray-400' : 'bg-white dark:bg-[#282828] shadow-lg rounded-lg border border-gray-300 dark:border-[#444444] text-gray-800 dark:text-gray-200'}`}>
                  Loading...
                </div>
              )}
            </div>
          )}

          <div className={isEmbedded ? 'space-y-2 pt-1' : 'space-y-2'}>
            <div className="flex justify-between items-center gap-4">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Last login</span>
              <span className="text-sm text-gray-600 dark:text-gray-400" style={isEmbedded ? { fontFamily: 'var(--chat-font-family)' } : {}}>{formatDateTime(lastLogin)}</span>
            </div>
            
            <div className="flex justify-between items-center gap-4">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Account created</span>
              <span className="text-sm text-gray-600 dark:text-gray-400" style={isEmbedded ? { fontFamily: 'var(--chat-font-family)' } : {}}>{formatDate(createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Organization name change confirmation modal */}
        <Modal
          isOpen={showCompanyModal}
          onClose={handleCompanyModalClose}
          onConfirm={handleCompanyModalConfirm}
          title="Update Organization Name"
          message={
            <div className="mb-4">
              <p className="mb-4">
                You are about to change the organization name from <span className="font-semibold">{user?.company?.name}</span> to <span className="font-semibold">{newCompanyName}</span>.
              </p>
              <p className="mb-4 text-yellow-600 dark:text-yellow-400">
                This will update the name for all members of your organization.
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Type "confirm" to proceed:
                </label>
                <input
                  type="text"
                  value={companyConfirmText}
                  onChange={(e) => setCompanyConfirmText(e.target.value)}
                  className="account-modal-input block w-full px-3 py-2 border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#444] focus:border-gray-300 dark:focus:border-[#444] dark:text-white"
                  placeholder="confirm"
                />
              </div>
            </div>
          }
          confirmText="Update Organization Name"
          cancelText="Cancel"
        />

        {/* Organization nickname change confirmation modal */}
        <Modal
          isOpen={showCompanyNicknameModal}
          onClose={handleCompanyNicknameModalClose}
          onConfirm={handleCompanyNicknameModalConfirm}
          title="Update Organization Nickname"
          message={
            <div className="mb-4">
              <p className="mb-4">
                {user?.company?.nickname ? (
                  <>
                    You are about to change the organization nickname from <span className="font-semibold">{user.company.nickname}</span> to <span className="font-semibold">{newCompanyNickname}</span>.
                  </>
                ) : (
                  <>
                    You are about to set the organization nickname to <span className="font-semibold">{newCompanyNickname}</span>.
                  </>
                )}
              </p>
              <p className="mb-4 text-yellow-600 dark:text-yellow-400">
                This will update the nickname for all members of your organization.
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Type "confirm" to proceed:
                </label>
                <input
                  type="text"
                  value={companyNicknameConfirmText}
                  onChange={(e) => setCompanyNicknameConfirmText(e.target.value)}
                  className="account-modal-input block w-full px-3 py-2 border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#444] focus:border-gray-300 dark:focus:border-[#444] dark:text-white"
                  placeholder="confirm"
                />
              </div>
            </div>
          }
          confirmText="Update Organization Nickname"
          cancelText="Cancel"
        />

        {/* Organization location change confirmation modal */}
        <Modal
          isOpen={showLocationModal}
          onClose={handleLocationModalClose}
          onConfirm={handleLocationModalConfirm}
          title="Update Organization Location"
          message={
            <div className="mb-4">
              <p className="mb-4">
                You are about to change the organization location from <span className="font-semibold">{user?.company?.location || 'No location'}</span> to <span className="font-semibold">{newLocation}</span>.
              </p>
              <p className="mb-4 text-yellow-600 dark:text-yellow-400">
                This will update the location for all members of your organization.
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Type "confirm" to proceed:
                </label>
                <input
                  type="text"
                  value={locationConfirmText}
                  onChange={(e) => setLocationConfirmText(e.target.value)}
                  className="account-modal-input block w-full px-3 py-2 border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#444] focus:border-gray-300 dark:focus:border-[#444] dark:text-white"
                  placeholder="confirm"
                />
              </div>
            </div>
          }
          confirmText="Update Organization Location"
          cancelText="Cancel"
        />
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={showCropModal}
        onClose={handleCropModalClose}
        onConfirm={handleCroppedImageConfirm}
        imageFile={selectedImageFile}
      />


    </>
  );
};

export default ProfileSection; 